/**
 * Autenticação, sessões e direitos da conta.
 *
 * Rotas em API 3 e 4, regras em SECURITY 2. Esquemas de entrada usam `.strict()`
 * para recusar campo desconhecido; esquemas de saída ficam permissivos para que
 * um campo novo no servidor não derrube um cliente antigo.
 */
import { z } from "zod";
import { AlphaKeySchema } from "./alpha.js";
import { DisplayNameSchema, EmailSchema, IdSchema, IsoDateTimeSchema, LocaleSchema, PasswordSchema, PlatformSchema, } from "./common.js";
// ---------------------------------------------------------------------------
// Usuário
// ---------------------------------------------------------------------------
/** Papel da conta (DATABASE 2.1). */
/**
 * `support` lê contas e compras, concede licença e estorna, lê denúncias e
 * chat, manda aviso e notificação. Não pune, não muda papel, não mexe em liga.
 */
export const RoleSchema = z.enum(["user", "moderator", "admin", "support"]);
/** Quem entra no painel administrativo, e por isso precisa do segundo fator. */
export const TEAM_ROLES = ["admin", "moderator", "support"];
/** Situação da conta (DATABASE 2.1). */
export const UserStatusSchema = z.enum(["active", "suspended", "deleted"]);
/** Usuário como ele volta para o próprio dono. */
export const UserSchema = z.object({
    id: IdSchema,
    /**
     * Nulo em conta que entrou pela Steam.
     *
     * Lá a compra e a identidade já vieram prontas, e pedir e-mail na abertura
     * seria cobrar um cadastro por algo que a loja já resolveu. O e-mail é pedido
     * quando a pessoa quiser levar a carreira para o navegador ou para o celular,
     * que é o que ele de fato resolve.
     */
    email: EmailSchema.nullable(),
    emailVerified: z.boolean(),
    displayName: DisplayNameSchema,
    avatarUrl: z.string().url().nullable(),
    locale: LocaleSchema,
    role: RoleSchema,
    status: UserStatusSchema,
    termsVersion: z.string().min(1),
    createdAt: IsoDateTimeSchema,
});
/**
 * Perfil público de outro usuário (`GET /users/:id/public`).
 *
 * Nunca traz e-mail: a saída é filtrada por DTO, como manda SECURITY 4.
 */
export const PublicProfileSchema = z.object({
    id: IdSchema,
    displayName: DisplayNameSchema,
    avatarUrl: z.string().url().nullable(),
    titles: z.number().int().min(0),
    reputation: z.number().min(0),
});
// ---------------------------------------------------------------------------
// Direitos
// ---------------------------------------------------------------------------
/** Origem de uma compra ou concessão (DATABASE 2.2). */
export const EntitlementSourceSchema = z.enum(["stripe", "pix", "play", "appstore", "grant"]);
/** Resumo de direitos de `GET /me/entitlements` (API 4). */
export const EntitlementsSchema = z.object({
    lifetime: z.boolean(),
    pass: z
        .object({
        active: z.boolean(),
        renewsAt: IsoDateTimeSchema.nullable(),
        source: EntitlementSourceSchema,
    })
        .nullable(),
    cosmetics: z.array(IdSchema),
});
// ---------------------------------------------------------------------------
// Dispositivo e sessão
// ---------------------------------------------------------------------------
/**
 * Impressão digital do dispositivo, montada no cliente (SECURITY 5).
 *
 * São só sinais que o navegador entrega sozinho, sem caixa de permissão e sem
 * pergunta nenhuma: fuso, idioma, resolução, núcleos, memória aproximada e
 * plataforma. **Nada de canvas nem de WebGL.** Os dois separam um pouco melhor
 * um aparelho do outro e custam caro: viram identificador de rastreamento,
 * exatamente o que LEGAL 1.4 promete que o jogo não faz. O ganho não paga a
 * promessa quebrada.
 *
 * Nenhum destes campos é gravado. O cliente coleta, o servidor deriva um hash
 * com sal e guarda só o hash, que serve para dizer "estes dois acessos vieram
 * do mesmo aparelho" e não serve para reconstruir aparelho nenhum.
 *
 * Cada valor é grosso de propósito. Resolução vem arredondada, memória vem em
 * gigabytes inteiros: a ideia é agrupar aparelhos parecidos, não separar um do
 * outro com precisão de impressão digital de verdade.
 */
export const DeviceFingerprintSchema = z
    .object({
    /** Fuso no formato IANA, como `America/Sao_Paulo`. */
    timezone: z.string().trim().min(1).max(64),
    /** Idioma preferido, como `pt-BR`. */
    language: z.string().trim().min(2).max(35),
    /** Resolução em pixels, já arredondada pelo cliente: `1920x1080`. */
    screen: z
        .string()
        .trim()
        .regex(/^\d{2,5}x\d{2,5}$/, "A resolução tem o formato `1920x1080`."),
    /** `navigator.hardwareConcurrency`. */
    cores: z.number().int().min(1).max(1024).optional(),
    /** `navigator.deviceMemory`, em gigabytes aproximados. */
    memory: z.number().min(0.25).max(1024).optional(),
    /** `navigator.platform` ou o equivalente da build nativa. */
    platform: z.string().trim().min(1).max(64).optional(),
})
    .strict();
/** Dispositivo informado no login, para a lista de sessões. */
export const DeviceSchema = z
    .object({
    name: z.string().trim().min(1).max(64),
    platform: PlatformSchema,
    /**
     * Opcional, e vai continuar opcional.
     *
     * Cliente antigo, build nativa sem os sinais e quem bloqueia o que o
     * navegador expõe continuam entrando normalmente. Login não é lugar de
     * exigir cooperação com a antifraude: quem se recusa a mandar o sinal
     * simplesmente não gera ligação, e a moderação não recebe caso nenhum.
     */
    fingerprint: DeviceFingerprintSchema.optional(),
    /**
     * Id de instalação do aplicativo, o mesmo que a licença registra.
     *
     * Só os aplicativos instalados mandam (Windows, Android, iOS): é sorteado
     * uma vez e guardado no cofre do sistema. É o único sinal que quer dizer
     * "mesmo aparelho" de verdade; a impressão acima quer dizer "parecido".
     */
    installId: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9_-]{16,64}$/)
        .optional(),
})
    .strict();
/** Sessão ativa, como aparece em `GET /auth/sessions`. */
export const SessionSchema = z.object({
    id: IdSchema,
    deviceName: z.string(),
    platform: PlatformSchema,
    /** Endereço de origem, já mascarado pelo servidor. */
    ip: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    lastUsedAt: IsoDateTimeSchema.nullable(),
    expiresAt: IsoDateTimeSchema,
    /** Marca a sessão que está fazendo a requisição. */
    current: z.boolean(),
});
// ---------------------------------------------------------------------------
// Entradas
// ---------------------------------------------------------------------------
/** `POST /auth/register`. */
export const RegisterRequestSchema = z
    .object({
    email: EmailSchema,
    password: PasswordSchema,
    displayName: DisplayNameSchema,
    acceptedTermsVersion: z.string().min(1).max(32),
    /*
     * A chave do alfa fechado, opcional no esquema e obrigatória na prática.
     *
     * Opcional aqui porque quem decide se ela é exigida é a bandeira
     * `alphaFechado`, que o painel liga e desliga com gente online: no dia em
     * que o alfa abrir, o campo some da tela e o servidor para de cobrar, sem
     * deploy e sem um esquema que passe a mentir. Exigir no esquema tornaria a
     * bandeira impossível de desligar sem subir versão nova.
     */
    alphaKey: AlphaKeySchema.optional(),
})
    .strict();
/** `POST /auth/verify-email`. */
export const VerifyEmailRequestSchema = z.object({ token: z.string().min(1).max(512) }).strict();
/** `POST /auth/login`. */
export const LoginRequestSchema = z
    .object({
    email: EmailSchema,
    password: PasswordSchema,
    device: DeviceSchema,
})
    .strict();
/** Provedores aceitos em `POST /auth/oauth/:provider`. */
export const OAuthProviderSchema = z.enum(["google", "apple"]);
/** `POST /auth/oauth/:provider`. */
export const OAuthLoginRequestSchema = z
    .object({
    idToken: z.string().min(1).max(4096),
    device: DeviceSchema,
})
    .strict();
/**
 * `POST /auth/refresh`.
 *
 * No web o refresh viaja em cookie `httpOnly` e o corpo vem vazio; nos apps
 * nativos ele vem no corpo (API 3, SECURITY 2).
 */
export const RefreshRequestSchema = z
    .object({ refreshToken: z.string().min(1).max(512).optional() })
    .strict();
/** `POST /auth/forgot`. Responde 204 exista ou não o e-mail. */
export const ForgotPasswordRequestSchema = z.object({ email: EmailSchema }).strict();
/** `POST /auth/reset`. */
export const ResetPasswordRequestSchema = z
    .object({
    token: z.string().min(1).max(512),
    password: PasswordSchema,
})
    .strict();
/** `PATCH /me`. Todo campo é opcional; só o que vier é alterado. */
export const UpdateMeRequestSchema = z
    .object({
    displayName: DisplayNameSchema.optional(),
    avatar: z.string().max(256).nullable().optional(),
    locale: LocaleSchema.optional(),
    notificationPrefs: z.record(z.boolean()).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Saídas
// ---------------------------------------------------------------------------
/** Resposta de `POST /auth/login`, `/auth/oauth/:provider` e `/auth/refresh`. */
export const AuthSessionResponseSchema = z.object({
    accessToken: z.string().min(1),
    /** Ausente no web, onde o refresh vai em cookie `httpOnly`. */
    refreshToken: z.string().min(1).optional(),
    user: UserSchema,
});
/** Resposta de `POST /auth/register`. */
export const RegisterResponseSchema = z.object({ user: UserSchema });
// ---------------------------------------------------------------------------
// Segundo fator (TOTP)
// ---------------------------------------------------------------------------
/** Um código do app: seis dígitos, com ou sem espaço no meio. */
export const TotpCodeSchema = z.string().regex(/^\d{3}\s?\d{3}$/, "Código de seis dígitos.");
/** Código de recuperação: oito letras e números, com ou sem o traço. */
export const RecoveryCodeSchema = z.string().regex(/^[A-Za-z0-9]{4}-?[A-Za-z0-9]{4}$/);
/** `GET /auth/totp`: onde a conta está no segundo fator. */
export const TotpStatusResponseSchema = z.object({
    /** Cadastrado e confirmado. */
    enabled: z.boolean(),
    /** Esta sessão já passou pelo código. */
    verified: z.boolean(),
    /** Quantos códigos de recuperação ainda valem. */
    recoveryCodesLeft: z.number().int(),
});
/** `POST /auth/totp/setup`: o segredo para cadastrar no app. */
export const TotpSetupResponseSchema = z.object({
    secret: z.string().min(1),
    /** `otpauth://`, para virar QR. */
    uri: z.string().min(1),
});
export const TotpEnableRequestSchema = z.object({ code: TotpCodeSchema }).strict();
/** `POST /auth/totp/enable`: os códigos de recuperação, mostrados uma vez só. */
export const TotpEnableResponseSchema = z.object({ recoveryCodes: z.array(z.string()) });
/** `POST /auth/totp/verify`: o código do app ou um de recuperação. */
export const TotpVerifyRequestSchema = z
    .object({ code: z.union([TotpCodeSchema, RecoveryCodeSchema]) })
    .strict();
/** `DELETE /auth/totp`: desligar exige o código de agora. */
export const TotpDisableRequestSchema = z.object({ code: TotpCodeSchema }).strict();
/** Resposta de `GET /auth/sessions`. */
export const SessionListResponseSchema = z.object({ items: z.array(SessionSchema) });
/** Resposta de `POST /v1/ws/ticket` (WEBSOCKET 1). */
export const WsTicketResponseSchema = z.object({
    ticket: z.string().min(1),
    /** Segundos de validade do ticket. */
    expiresIn: z.number().int().positive(),
});
/** Resposta de `GET /v1/meta` (API 2). */
export const MetaResponseSchema = z.object({
    apiVersion: z.string().min(1),
    engineVersion: z.string().min(1),
    protocolVersion: z.number().int().positive(),
    minClientVersion: z.string().min(1),
    features: z.record(z.boolean()),
});
//# sourceMappingURL=auth.js.map